import React from 'react'
import { JsonLd } from 'react-schemaorg'
import { type Event } from 'schema-dts'

import {
  getImageUrl,
  keywordsFromTags,
  structuredTextToPlain
} from '@/utils/microdata'

const monthToNumber: Record<string, string> = {
  January: '01',
  February: '02',
  March: '03',
  April: '04',
  May: '05',
  June: '06',
  July: '07',
  August: '08',
  September: '09',
  October: '10',
  November: '11',
  December: '12'
}

const parseDay = (day?: string | null): string | undefined => {
  if (!day) return undefined
  const digits = day.replace(/\D+/g, '')
  return digits.padStart(2, '0')
}

const buildIsoDate = (
  year?: string | null,
  month?: string | null,
  day?: string | null
): string | undefined => {
  if (!year || !month || !day) return undefined
  const m = monthToNumber[month]
  const d = parseDay(day)
  if (!m || !d) return undefined
  return `${year}-${m}-${d}`
}

type Meta = {
  firstPublishedAt?: string | null
  publishedAtTimestamp?: string | null
}

type Props = {
  data: Exclude<Queries.EventQuery['datoCmsPctEvent'], null>
  url: string
}

const MicrodataEvent: React.FC<Props> = ({ data, url }) => {
  const {
    meta,
    externalName,
    excerpt,
    image,
    seo,
    tags,
    location,
    dateInitialYear,
    dateInitialMonth,
    dateInitialDay,
    dateEndMonth,
    dateEndDay,
    registrationUrl,
    registrationTitle,
    registrationDescription,
    intro
  } = data

  const startDate = buildIsoDate(
    dateInitialYear,
    dateInitialMonth,
    dateInitialDay
  )
  const endDate = buildIsoDate(
    dateInitialYear,
    dateEndMonth ?? dateInitialMonth,
    dateEndDay ?? dateInitialDay
  )
  const keywords = keywordsFromTags(tags)
  const img = getImageUrl(seo?.image ?? image)
  const introText = structuredTextToPlain(intro)
  const description = introText ?? excerpt ?? ''

  const offers = registrationUrl
    ? {
        '@type': 'Offer' as const,
        url: registrationUrl,
        name: registrationTitle ?? undefined,
        description: registrationDescription ?? undefined,
        availability: 'https://schema.org/InStock' as const
      }
    : undefined

  const place = location
    ? {
        '@type': 'Place' as const,
        name: location
      }
    : undefined

  return (
    <JsonLd<Event>
      item={{
        '@context': 'https://schema.org',
        '@type': 'Event',
        name: externalName ?? '',
        description,
        image: img,
        startDate,
        endDate,
        location: place,
        keywords,
        eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
        offers,
        url
      }}
    />
  )
}

export default MicrodataEvent
